import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from '_common/hooks';
import { SectionHeading, RadioGroup } from '@ets/design-system';
import { LANGUAGES } from 'Intl/Intl';
import { openAndUpdateModal, openModal } from '_common/modals/ModalsSlice';
import { useGetCurrentUserQuery, useGetTenantSettingsQuery } from '_common/services/api/authority';
import { useEditProfileMutation } from '../UserSettingsApi';

import { UserAvatar } from '_common/components';
import { parseTimezoneLabel } from '_common/modals/EditTimezoneModal/TimezoneSelect';
import { Setting } from 'Settings/components';

import styles from './UserProfile.module.scss';

const UserProfile = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const locale = useSelector((state) => state.intl.locale);
  const [editProfile] = useEditProfileMutation();
  const { data: authUser } = useGetCurrentUserQuery();
  const userProfile = authUser?.profile;

  const { data: tenantSettings } = useGetTenantSettingsQuery();

  const disabledSettings = useMemo(() => {
    const settings: {
      [x in Exclude<
        typeof tenantSettings,
        undefined
      >['not_editable_profile_fields'][number]]: boolean;
    } = {
      company: false,
      department: false,
      language: false,
      timezone: false,
      date_format: false,
      time_format: false,
      first_name: false,
      last_name: false,
    };

    tenantSettings?.not_editable_profile_fields?.forEach((setting) => {
      settings[setting] = true;
    });

    return settings;
  }, [tenantSettings]);

  if (!userProfile) {
    return null;
  }

  const handleOpenGenericSettingModal = ({
    header,
    label,
    value,
    inputPlaceholder,
    action,
    actionValues,
  }: {
    header: string;
    label: string;
    value: string;
    inputPlaceholder: string;
    action: string;
    actionValues: { setting: string };
  }) => {
    dispatch(
      openAndUpdateModal({
        modal: 'GenericSettingModal',
        data: { header, label, value, inputPlaceholder, action, actionValues },
      }),
    );
  };

  const openLanguageSettingModal = () => {
    dispatch(openModal('LanguageSettingModal'));
  };

  const openChangeNameModal = () => {
    dispatch(
      openAndUpdateModal({
        modal: 'ChangeNameModal',
        data: {
          disabledFields: {
            firstName: disabledSettings.first_name,
            lastName: disabledSettings.last_name,
          },
        },
      }),
    );
  };

  const openChangeEmailModal = () => {
    dispatch(openModal('ChangeEmailModal'));
  };

  const openEditTimezoneModal = () => {
    dispatch(openModal('EditTimezoneModal'));
  };

  const openEditCompany = () => {
    handleOpenGenericSettingModal({
      header: intl.formatMessage({ id: 'settings.company.header' }),
      label: intl.formatMessage({ id: 'settings.user.company' }),
      value: userProfile.company,
      inputPlaceholder: intl.formatMessage({ id: 'COMPANY_PLACEHOLDER' }),
      action: 'editProfile',
      actionValues: { setting: 'company' },
    });
  };

  const openEditDepartment = () => {
    handleOpenGenericSettingModal({
      header: intl.formatMessage({ id: 'settings.department.header' }),
      label: intl.formatMessage({ id: 'settings.user.department' }),
      value: userProfile.department,
      inputPlaceholder: intl.formatMessage({ id: 'DEPARTMENT_PLACEHOLDER' }),
      action: 'editProfile',
      actionValues: { setting: 'department' },
    });
  };

  const openChangeAvatarModal = () => {
    dispatch(openModal('ChangeAvatarModal'));
  };

  const TIMEFORMATS: { label: string; value: '12h' | '24h' }[] = [
    { label: intl.formatMessage({ id: 'settings.user.hour' }, { value: 12 }), value: '12h' },
    { label: intl.formatMessage({ id: 'settings.user.hour' }, { value: 24 }), value: '24h' },
  ];

  const DATEFORMATS: { label: string; value: 'dm' | 'md' }[] = [
    { label: 'DD/MM/YYYY', value: 'dm' },
    { label: 'MM/DD/YYYY', value: 'md' },
  ];

  return (
    <>
      <div className={styles.container}>
        <div>
          <SectionHeading
            size="l"
            sticky
            title={intl.formatMessage({ id: 'settings.user.profile' })}
            testId="profile"
          />
          {/* Avatar */}
          <Setting
            style={{ alignItems: 'flex-start', height: 'auto' }}
            label={intl.formatMessage({ id: 'settings.user.avatar' })}
            onEdit={openChangeAvatarModal}
            onEditLabel="global.edit"
            testId="avatar"
          >
            <UserAvatar userId={userProfile.id} size="xl" />
          </Setting>
          {/* Email */}
          <Setting
            label={intl.formatMessage({ id: 'settings.user.email' })}
            onEdit={openChangeEmailModal}
            onEditLabel="global.edit"
            testId="email"
          >
            {userProfile.email}
          </Setting>
          {/* Username */}
          <Setting label={intl.formatMessage({ id: 'settings.user.username' })} testId="username">
            {userProfile.username}
          </Setting>
          {/* Name TODO: fix prego */}
          <Setting
            label={intl.formatMessage({ id: 'settings.user.name' })}
            onEdit={openChangeNameModal}
            onEditLabel="global.edit"
            editable={!(disabledSettings.last_name && disabledSettings.first_name)}
            testId="name"
          >{`${userProfile.first_name} ${userProfile.last_name}`}</Setting>
        </div>

        <div>
          <SectionHeading
            size="l"
            sticky
            style={{ margin: '5rem 0 0 0' }}
            title={intl.formatMessage({ id: 'settings.user.company' })}
            testId="company"
          />
          {/* Company */}
          <Setting
            label={intl.formatMessage({ id: 'settings.user.company' })}
            onEdit={openEditCompany}
            onEditLabel="global.edit"
            testId="company"
            editable={!disabledSettings.company}
          >
            {userProfile.company}
          </Setting>
          {/* Department */}
          <Setting
            label={intl.formatMessage({ id: 'settings.user.department' })}
            onEdit={openEditDepartment}
            onEditLabel="global.edit"
            testId="department"
            editable={!disabledSettings.department}
          >
            {userProfile.department}
          </Setting>
        </div>

        <div>
          <SectionHeading
            size="l"
            sticky
            style={{ margin: '5rem 0 0 0' }}
            title={intl.formatMessage({ id: 'settings.user.preferences' })}
            testId="preferences"
          />
          {/* Languages */}
          <Setting
            label={intl.formatMessage({ id: 'settings.user.language' })}
            onEdit={openLanguageSettingModal}
            onEditLabel="global.edit"
            testId="language"
            editable={!disabledSettings.language}
          >
            {
              Object?.values(LANGUAGES)?.find(
                (language) => language.code === locale || language.code === locale.split('-')[0],
              )?.name
            }
          </Setting>
          {/* Timezone */}
          <Setting
            label={intl.formatMessage({ id: 'settings.user.timezone' })}
            onEdit={openEditTimezoneModal}
            onEditLabel="global.edit"
            testId="timezone"
            editable={!disabledSettings.timezone}
          >
            {parseTimezoneLabel(userProfile.timezone)}
          </Setting>
          {/* Date format */}
          <Setting
            style={{ height: 'auto' }}
            label={intl.formatMessage({ id: 'settings.user.dateFormat' })}
            testId="date-format"
          >
            <RadioGroup
              items={DATEFORMATS}
              value={userProfile.date_format}
              onChange={(e) => editProfile({ date_format: e.target.value })}
              testId="date-format"
              direction="vertical"
              size="s"
              disabled={disabledSettings.date_format}
              className={styles.radiogroup}
            />
          </Setting>
          {/* Time format */}
          <Setting
            style={{ height: 'auto' }}
            label={intl.formatMessage({ id: 'settings.user.timeFormat' })}
            testId="time-format"
          >
            <RadioGroup
              items={TIMEFORMATS}
              value={userProfile.time_format}
              onChange={(e) => editProfile({ time_format: e.target.value })}
              testId="time-format"
              direction="vertical"
              size="s"
              disabled={disabledSettings.time_format}
              className={styles.radiogroup}
            />
          </Setting>
        </div>
      </div>
    </>
  );
};

export default UserProfile;
